import { useSelector } from "react-redux";
import { PieChart, Pie, Cell } from "recharts";
import { ChartContainer, Skeleton, useIsMobile } from "@hotelise/ui";

import { getDashboardYearReportData } from "state/modules/dashboard";

interface Props {
	isLoading: boolean;
}

const OccupancyRate = ({ isLoading }: Props) => {
	const report = useSelector(getDashboardYearReportData);
	const isMobile = useIsMobile();

	const occupancyRate = Math.trunc(report?.occupancyRate || 0);
	const vacantRate = 100 - occupancyRate;

	const occupancyData = [
		{ name: "Occupied", value: occupancyRate, fill: "hsl(var(--primary))" },
		{ name: "Vacant", value: vacantRate, fill: "hsl(var(--muted))" },
	];

	if (isLoading) {
		return (
			<div className="bg-card rounded-lg p-6 border">
				<Skeleton className="h-6 w-40 mb-4" />
				<div className="flex items-center justify-center h-[280px]">
					<Skeleton className="h-[200px] w-[200px] rounded-full" />
				</div>
				<div className="mt-4 space-y-2">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Skeleton className="w-3 h-3 rounded-full" />
							<Skeleton className="h-4 w-16" />
						</div>
						<Skeleton className="h-4 w-10" />
					</div>
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Skeleton className="w-3 h-3 rounded-full" />
							<Skeleton className="h-4 w-16" />
						</div>
						<Skeleton className="h-4 w-10" />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-card rounded-lg p-6 border">
			<h3 className="text-lg font-semibold mb-4">Occupancy Rate YTD</h3>

			<div className="relative">
				<ChartContainer
					config={{}}
					className="h-[280px] w-full [&>div]:!aspect-auto"
				>
					<PieChart>
						<Pie
							data={occupancyData}
							cx="50%"
							cy="50%"
							innerRadius={60}
							outerRadius={100}
							startAngle={90}
							endAngle={-270}
							dataKey="value"
						>
							{occupancyData.map((entry) => (
								<Cell key={entry.name} fill={entry.fill} />
							))}
						</Pie>
					</PieChart>
				</ChartContainer>

				{/* Center Text */}
				<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
					<div className="text-center">
						<div
							className={`${isMobile ? "text-3xl" : "text-4xl"} font-bold text-primary`}
						>
							{occupancyRate}%
						</div>
						<div className="text-sm text-muted-foreground mt-1">
							Occupied
						</div>
					</div>
				</div>
			</div>

			<div className="mt-4 space-y-2">
				<div className="flex items-center justify-between text-sm">
					<div className="flex items-center gap-2">
						<div className="w-3 h-3 rounded-full bg-primary" />
						<span>Occupied</span>
					</div>
					<span className="font-medium">{occupancyRate}%</span>
				</div>
				<div className="flex items-center justify-between text-sm">
					<div className="flex items-center gap-2">
						<div className="w-3 h-3 rounded-full bg-muted" />
						<span>Vacant</span>
					</div>
					<span className="font-medium">{vacantRate}%</span>
				</div>
			</div>
		</div>
	);
};

export default OccupancyRate;
